import { Box, Button, CircularProgress, List, ListItem, TextField } from "@mui/material";
import { FileOrLink, IAttachmentTypeListItem, IFileAttachment } from "models/attachments";
import AttachmentTypeList from "./AttachmentTypeList";
import { useEffect, useState } from "react";
import AttachmentFormatSelection from "./AttachmentFormatSelection";
import useExistingAttachmentsColumns from "./useExistingAttachmentsColumns";
import { useAttachmentSearch } from "hooks/attachments";
import { FilterOp, FilterParam, GridMetaData, Sort } from "models/grids";
import { DataGridPro, GridFilterModel, GridLogicOperator, GridPaginationModel, GridRowSelectionModel, GridSortModel } from "@mui/x-data-grid-pro";
import { Ico_Filter_Active_Component, Ico_Filter_Component, Ico_MenuDots, Ico_SortAscending, Ico_SortDescending } from "@sagecentral/core-ui";
import { GridBox, GridNoRowsOverlay, GridToolbar } from "components/shared/grids";
import { customLocaleText } from "models/grids/HeaderFilterLocaleText";

type ExistingAttachmentFormProps = {
    types: IAttachmentTypeListItem[] | null;    
    onClose: () => void;
    onSave: (ids: number[], filenames: string, typeId: number, fileOrLink: FileOrLink) => Promise<void>;
    saving: boolean;
    workItemId?: number;
    draft?: boolean;
};

const NoRowsOverlay = () => {
    return (
        <GridNoRowsOverlay text={`No attachments found.`} />
    );
};

const ExistingAttachmentForm: React.FC<ExistingAttachmentFormProps> = (props) => {
    const { types, onSave, onClose, saving, workItemId } = props;

    const [ typeId, setTypeId ] = useState<number>(0);
    const [ format, setFormat ] = useState<FileOrLink>(FileOrLink.File);
    const [ workItemName, setWorkItemName ] = useState<string>('');
    const [ attachmentName, setAttachmentName ] = useState<string>('');
    const [selectedWorkItemId, setSelectedWorkItemId] = useState<number | undefined>(workItemId);

    const [ data, setData ] = useState<IFileAttachment[]>([]);
    const [ total, setTotal ] = useState<number>(0);

    const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
    const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
    const [sortModel, setSortModel] = useState<GridSortModel>([{field: 'modifiedOn', sort: 'desc'}]);
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
    
    const [ columns ] = useExistingAttachmentsColumns();
    const [ search, searching ] = useAttachmentSearch();
    const islinkRequired = typeId !== 0 && types?.find(type => type.id == typeId)?.linkRequired && !types?.find(type => type.id == typeId)?.fileRequired;

    useEffect(() => {
        handleSearch();
    }, [ paginationModel, sortModel, filterModel ]);

    useEffect(() => {
        if(islinkRequired == true){
            setFormat(FileOrLink.Link);
        }
    }, [islinkRequired])

    const buildGridMetadata = () => {
        const sort: Sort[] = [];
        const filter: FilterParam = { filters: [], logic: GridLogicOperator.And };
        if (sortModel && sortModel.length > 0) {
            sortModel.forEach((sm) => {
                sort.push({
                    field: sm.field,
                    dir: sm.sort
                });
            });
        }
        if (filterModel && filterModel.items.length > 0) {
            filterModel.items.forEach((fm) => {
                filter.filters.push({
                    field: fm.field,
                    operator: fm.operator as FilterOp,
                    value: fm.value
                });
            });
        }
        const metaData: GridMetaData = {
            page: paginationModel.page + 1,
            pageSize: paginationModel.pageSize,
            sort: sort,
            filterParam: filter
        };

        return metaData;
    };

    const handleSearch = async () => {
        if (typeId === 0) return;
        setData([]);
        
        const gridMetadata = buildGridMetadata();
        const result = await search(gridMetadata, {
            typeId,
            fileOrLink: format,
            workItemName,
            attachmentName,
            workItemId: selectedWorkItemId
        }, workItemId!, props.draft);

        if (result === null) {
            setData([]);
            setTotal(0);
            return;
        }

        setData(result.attachments);
        setTotal(result.total);
    };

    const handleSaveClick = () => {
        let filenames: string[] = [];
        let ids: number[] = [];

        rowSelectionModel.forEach(row => {
            const element = (row as string).split(',');
            ids.push(parseInt(element[0]));
            filenames.push(element[1]);
        });

        onSave(ids, filenames.join('|'), typeId, format);
    };

    const isFilterApplied = () => ((filterModel?.items.length || 0) > 0);
    const getData = () => data;

    return (
        <>
            <List>
                <ListItem>
                    <AttachmentTypeList
                        types={types}
                        value={typeId}
                        onChanged={setTypeId}
                    />
                </ListItem>
                <ListItem>
                    <AttachmentFormatSelection
                        disabled={typeId === 0}
                        value={format}
                        onChanged={setFormat}
                        islinkRequired={islinkRequired}
                    />
                </ListItem>
                <ListItem>
                    <TextField
                            id="workItemName"
                            name="workItemName"
                            label="Search by Work Item Name:"
                            fullWidth                            
                            disabled={typeId === 0}
                            value={workItemName}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setWorkItemName(event.target.value)}
                            required={false}
                    />
                </ListItem>
                <ListItem>
                    <TextField
                            id="attachmentName"
                            name="attachmentName"
                            label="Search by Attachment Name:"
                            fullWidth                            
                            disabled={typeId === 0}
                            value={attachmentName}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setAttachmentName(event.target.value)}
                            required={false}
                    />
                </ListItem>
                <ListItem sx={{justifyContent:'right'}}>
                    <Button disabled={typeId === 0} 
                        variant="contained" 
                        onClick={handleSearch}
                    >
                        Search
                    </Button>
                </ListItem>
                <ListItem >
                    <GridBox getData={getData}>
                        <Box sx={{ height: "50vh" }}>
                            <DataGridPro
                                sx={{'& .MuiDataGrid-columnHeaderCheckbox': {
                                    pl: '0px', pr:'5px'
                                    }, 
                                    '& .MuiDataGrid-columnHeader': {
                                        fontWeight: 'bold',
                                        },
                                    '& .MuiDataGrid-columnHeaderTitle': {
                                        fontWeight: 'bold',
                                        },
                                    '& .MuiDataGrid-cellCheckbox': {
                                        pl: '15px',
                                        '& .Mui-disabled': {
                                            display: 'none'
                                        }
                                    },
                                    '.MuiDataGrid-cellContent': {
                                        pointerEvents: 'none',
                                    },
                                }}
                                rows={getData()}
                                rowCount={total}
                                columns={columns}
                                getRowId={(r: any) => r.workItemInstanceAttachmentID+","+r.attachmentName+","+r.attachmentTypeID}
                                filterMode="server"
                                onRowSelectionModelChange={(newRowSelectionModel) => {
                                    setRowSelectionModel(newRowSelectionModel);
                                    }}
                                rowSelectionModel={rowSelectionModel}
                                checkboxSelection
                                keepNonExistentRowsSelected={true}
                                disableRowSelectionOnClick
                                filterModel={filterModel}
                                sortingMode="server"
                                sortModel={sortModel}
                                paginationMode='server'
                                pagination={true}    
                                pageSizeOptions={[25, 50, 75, 100]}
                                paginationModel={paginationModel}
                                onSortModelChange={(model) => setSortModel(model)}
                                onFilterModelChange={(model) => setFilterModel(model)}
                                onPaginationModelChange={(model) => setPaginationModel(model)}
                                slots={{
                                    loadingOverlay: () => (<CircularProgress/>),
                                    columnMenuSortAscendingIcon: Ico_SortAscending,
                                    columnMenuSortDescendingIcon: Ico_SortDescending,
                                    columnSortedAscendingIcon: Ico_SortAscending,
                                    columnSortedDescendingIcon: Ico_SortDescending,
                                    columnUnsortedIcon: null,
                                    moreActionsIcon: Ico_MenuDots,
                                    openFilterButtonIcon: isFilterApplied() ? Ico_Filter_Active_Component : Ico_Filter_Component,
                                    quickFilterIcon: Ico_Filter_Component,
                                    columnFilteredIcon: Ico_Filter_Component,
                                    quickFilterClearIcon: Ico_Filter_Component,
                                    columnMenuFilterIcon: Ico_Filter_Component,
                                    noRowsOverlay: () => NoRowsOverlay(),
                                    toolbar: GridToolbar,
                                }}
                                headerFilters
                                localeText={customLocaleText}
                                slotProps={{filterPanel: {logicOperators: [GridLogicOperator.And]}, panel: { placement: "bottom-end" }}}
                                loading={searching || !data}
                                initialState={{
                                    filter: {filterModel},
                                    sorting: {sortModel},
                                    pagination: {paginationModel},
                                }}
                                getRowClassName={(params: any) =>
                                    params.indexRelativeToCurrentPage % 2 === 0 ? 'row even' : 'row odd'
                                }
                            />
                        </Box>
                    </GridBox>
                </ListItem>
                <ListItem>
                    <Button variant="outlined" sx={{width:'50%'}} onClick={onClose}>Cancel</Button>
                    <Button variant='contained' sx={{width:'50%'}} 
                        disabled={ rowSelectionModel.length === 0 || saving} 
                        onClick={handleSaveClick}
                        endIcon={saving ? <CircularProgress 
                            size={20}
                            sx={{                                
                                position: 'unset',
                                color: (theme) => theme.palette.primary.contrastText
                            }}
                            
                        /> : null}
                    >
                        Save
                    </Button>
                </ListItem>
            </List>
        </>
    );
};

export default ExistingAttachmentForm;